import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

function AdminPaymentLogs() {
    const { token } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedLog, setExpandedLog] = useState(null);
    
    // Filtreler
    const [filters, setFilters] = useState({
        paymentId: '',
        logLevel: '',
        action: '',
        startDate: '',
        endDate: '',
        page: 1,
        limit: 50
    });

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = new URLSearchParams();
            if (filters.paymentId) params.append('paymentId', filters.paymentId);
            if (filters.logLevel) params.append('logLevel', filters.logLevel);
            if (filters.action) params.append('action', filters.action);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('page', filters.page);
            params.append('limit', filters.limit);

            const response = await api.get(`/api/payment/admin/logs?${params.toString()}`);
            const data = response.data || response;
            setLogs(data.logs || []);
        } catch (err) {
            setError('Loglar yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const getLogLevelBadge = (level) => {
        const badges = {
            info: 'bg-blue-100 text-blue-800',
            warning: 'bg-yellow-100 text-yellow-800',
            error: 'bg-red-100 text-red-800',
            debug: 'bg-gray-100 text-gray-800'
        };
        return badges[level] || 'bg-gray-100 text-gray-800';
    };

    const getLogLevelIcon = (level) => {
        const icons = {
            info: 'ℹ️',
            warning: '⚠️',
            error: '❌',
            debug: '🔧'
        };
        return icons[level] || '📝';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('tr-TR');
    };

    const toggleExpand = (logId) => {
        setExpandedLog(expandedLog === logId ? null : logId);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Payment Logs</h1>

            {/* Filtreler */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Payment ID
                        </label>
                        <input
                            type="text"
                            value={filters.paymentId}
                            onChange={(e) => setFilters({ ...filters, paymentId: e.target.value, page: 1 })}
                            placeholder="UUID"
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Log Level
                        </label>
                        <select
                            value={filters.logLevel}
                            onChange={(e) => setFilters({ ...filters, logLevel: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">Tümü</option>
                            <option value="info">Info</option>
                            <option value="warning">Warning</option>
                            <option value="error">Error</option>
                            <option value="debug">Debug</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Action
                        </label>
                        <select
                            value={filters.action}
                            onChange={(e) => setFilters({ ...filters, action: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">Tümü</option>
                            <option value="initialize">Initialize</option>
                            <option value="callback">Callback</option>
                            <option value="payment_success">Payment Success</option>
                            <option value="payment_failed">Payment Failed</option>
                            <option value="refund">Refund</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <button
                        onClick={() => setFilters({ paymentId: '', logLevel: '', action: '', startDate: '', endDate: '', page: 1, limit: 50 })}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md"
                    >
                        Filtreleri Temizle
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Logs Listesi */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p className="mt-2 text-gray-600">Yükleniyor...</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {logs.map((log) => (
                        <div 
                            key={log.id}
                            className={`bg-white rounded-lg shadow border-l-4 ${
                                log.log_level === 'error' ? 'border-red-500' :
                                log.log_level === 'warning' ? 'border-yellow-500' :
                                log.log_level === 'info' ? 'border-blue-500' :
                                'border-gray-500'
                            } overflow-hidden`}
                        >
                            <div 
                                className="p-4 cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleExpand(log.id)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start space-x-3 flex-1">
                                        <span className="text-2xl">{getLogLevelIcon(log.log_level)}</span>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded ${getLogLevelBadge(log.log_level)}`}>
                                                    {log.log_level.toUpperCase()}
                                                </span>
                                                <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
                                                    {log.action}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {formatDate(log.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-sm font-medium text-gray-900">{log.message}</p>
                                            {log.payments && (
                                                <p className="text-xs text-gray-600 mt-1">
                                                    Payment ID: <span className="font-mono">{log.payment_id}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button className="text-gray-400 hover:text-gray-600">
                                        {expandedLog === log.id ? '▼' : '▶'}
                                    </button>
                                </div>

                                {/* Expanded Details */}
                                {expandedLog === log.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 uppercase">Log ID</label>
                                                <p className="text-sm font-mono text-gray-900 break-all">{log.id}</p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 uppercase">Payment ID</label>
                                                <p className="text-sm font-mono text-gray-900 break-all">{log.payment_id || 'N/A'}</p>
                                            </div>
                                        </div>

                                        {log.error_code && (
                                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                                                <label className="block text-xs font-medium text-red-700 uppercase mb-1">Error Code</label>
                                                <p className="text-sm text-red-800 font-mono">{log.error_code}</p>
                                            </div>
                                        )}

                                        {log.context && Object.keys(log.context).length > 0 && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Context</label>
                                                <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                                                    {JSON.stringify(log.context, null, 2)}
                                                </pre>
                                            </div>
                                        )}

                                        {log.payments && (
                                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                                <label className="block text-xs font-medium text-blue-700 uppercase mb-1">Payment Info</label>
                                                <div className="text-sm text-blue-800">
                                                    <p>Order ID: <span className="font-mono">{log.payments.order_id}</span></p>
                                                    <p>Amount: {log.payments.amount} {log.payments.currency || 'TRY'}</p>
                                                    <p>Status: <span className="font-semibold">{log.payments.status}</span></p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {logs.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-lg shadow">
                            <p className="text-gray-500">Log bulunamadı</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AdminPaymentLogs;
